import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Upload, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PROPERTY_FIELDS = [
  ["district", "District"], ["taluka", "Taluka"], ["village", "Village / City"], ["sro", "SRO (Sub-Registrar Office)"],
  ["survey_number", "Survey Number"], ["document_number", "Document Number"],
  ["registration_year", "Registration Year"], ["owner_name", "Owner Name"],
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function NewOrder() {
  const { slug } = useParams();
  const { user } = useApp();
  const navigate = useNavigate();
  const isPack = slug?.startsWith("pack-");
  const realSlug = isPack ? slug.replace("pack-", "") : slug;
  const [item, setItem] = useState(null);
  const [step, setStep] = useState(2);
  const [details, setDetails] = useState({});
  const [files, setFiles] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/login", { state: { next: `/order/new/${slug}` } }); return; }
    const url = isPack ? `/packs` : `/services/${realSlug}`;
    api.get(url).then((r) => setItem(isPack ? r.data.find((p) => p.slug === realSlug) : r.data)).catch(() => toast.error("Not found"));
  }, [slug, user, isPack, realSlug, navigate]);

  const createOrder = async () => {
    if (!terms) { setTermsError("You must accept the Terms & Conditions"); return; }
    setTermsError("");
    try {
      const { data } = await api.post("/orders", {
        service_slug: isPack ? "" : realSlug, pack_slug: isPack ? realSlug : "",
        coupon_code: coupon, property_details: details, terms_accepted: true,
      });
      setOrder(data);
      setStep(4);
      toast.success(`Order ${data.order_id} created`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !order) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/upload`, fd);
      setFiles([...files, data.file]);
      toast.success("File uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/pay`);
      if (data.mock) {
        await api.post(`/orders/${order.order_id}/verify-payment`, { mock: true });
        toast.success("Payment successful (mock mode — Razorpay keys not configured)");
        setStep(6);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        order_id: data.razorpay_order_id, name: "DocGalaxy Gujarat",
        description: order.item_name, prefill: data.prefill,
        handler: async (resp) => {
          await api.post(`/orders/${order.order_id}/verify-payment`, resp);
          toast.success("Payment verified");
          setStep(6);
        },
        theme: { color: "#0f3d91" },
      });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPaying(false); }
  };

  if (!item) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">Loading...</div>;

  const discount = coupon.toUpperCase() === "WELCOME10" ? Math.round(item.price * 0.1) : 0;
  const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="order-wizard">
      <Seo title={`Request ${item.name_en}`} description={item.desc_en} />
      <div className="flex items-center gap-2 mb-6 overflow-x-auto" data-testid="wizard-steps">
        {["Details", "Upload", "Price", "Payment", "Done"].map((s, i) => (
          <div key={s} className={`flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap ${step >= i + 2 ? "text-secondary" : "text-muted-foreground"}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${step >= i + 2 ? "bg-secondary text-white" : "bg-muted"}`}>{i + 1}</span>
            {s}
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-xl p-6">
        <h1 className="font-display font-bold text-2xl">{item.name_en} <span className="text-muted-foreground text-base font-normal">/ {item.name_gu}</span></h1>
        <p className="text-sm text-muted-foreground mt-1">{item.desc_en}</p>

        {step === 2 && (
          <div className="mt-6" data-testid="wizard-step-details">
            <h3 className="font-semibold mb-3">Property details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {PROPERTY_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-muted-foreground">{label}</label>
                  <input className={input} value={details[k] || ""} onChange={(e) => setDetails({ ...details, [k]: e.target.value })} data-testid={`order-field-${k}`} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-muted-foreground">Coupon code (optional)</label>
              <input className={input} value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="WELCOME10" data-testid="order-coupon-input" />
            </div>
            <div className="mt-4">
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="order-terms-label">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={terms}
                  onChange={(e) => { setTerms(e.target.checked); if (e.target.checked) setTermsError(""); }} data-testid="order-terms-checkbox" />
                <span>I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline underline-offset-2" data-testid="order-terms-link">Terms &amp; Conditions</Link></span>
              </label>
              {termsError && <p className="text-xs text-red-600 mt-1.5" role="alert" data-testid="order-terms-error">{termsError}</p>}
            </div>
            <Button className="mt-5 bg-primary" onClick={createOrder} data-testid="order-create-btn">Continue</Button>
          </div>
        )}

        {step === 4 && order && (
          <div className="mt-6" data-testid="wizard-step-upload">
            <h3 className="font-semibold mb-2">Upload supporting documents (optional)</h3>
            <p className="text-xs text-muted-foreground mb-3">PDF / JPG / PNG up to 5MB each. Stored privately.</p>
            <label className="flex items-center gap-2 border-2 border-dashed border-border rounded-lg p-5 cursor-pointer hover:border-primary transition-colors" data-testid="order-upload-dropzone">
              <Upload size={20} className="text-primary" />
              <span className="text-sm">Choose file to upload</span>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={upload} data-testid="order-upload-input" />
            </label>
            {files.map((f) => (
              <div key={f.id} className="mt-2 text-sm flex items-center gap-2 text-secondary"><CheckCircle2 size={14} /> {f.name}</div>
            ))}
            <div className="mt-5 bg-muted rounded-lg p-4 space-y-1" data-testid="order-price-summary">
              <div className="flex justify-between text-sm"><span>Price</span><span>₹{order.price}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-sm text-secondary"><span>Discount ({order.coupon_code})</span><span>-₹{order.discount}</span></div>}
              <div className="flex justify-between font-bold border-t border-border pt-2"><span>Total</span><span data-testid="order-total">₹{order.total}</span></div>
            </div>
            <Button className="mt-4 bg-secondary hover:bg-secondary/90 h-11 px-8" onClick={pay} disabled={paying} data-testid="order-pay-btn">
              {paying ? "Processing..." : `Pay ₹${order.total}`}
            </Button>
          </div>
        )}

        {step === 6 && order && (
          <div className="mt-6 text-center" data-testid="wizard-step-done">
            <CheckCircle2 size={56} className="text-secondary mx-auto" />
            <h3 className="font-display font-bold text-xl mt-3">Order placed successfully</h3>
            <p className="text-muted-foreground text-sm mt-1">Your order ID</p>
            <p className="font-mono font-bold text-2xl text-primary" data-testid="order-id-display">{order.order_id}</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button onClick={() => navigate(`/order/${order.order_id}`)} data-testid="order-track-btn">Track Order</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="order-dashboard-btn">Dashboard</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
